import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../../supabaseClient";

const normalize = (value) => String(value || "").trim().toLowerCase();

// A collector may naturally type a physical copy as "Red 3/5". Keep the
// reusable variant as "Red /5" and retain only the copy number on user_items.
const parseNumberedVariantLabel = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^(.*?\S)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (!match) return null;

  const serialNumber = Number(match[2]);
  const serialTotal = Number(match[3]);
  if (serialNumber < 1 || serialTotal < 1 || serialNumber > serialTotal) return null;

  return {
    name: `${match[1].trim()} /${serialTotal}`,
    serialNumber,
    serialTotal,
  };
};

const parseSerialField = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const match = raw.match(/^(\d+)(?:\s*\/\s*(\d+))?$/);
  if (!match) return null;
  return {
    serialNumber: Number(match[1]),
    serialTotal: match[2] ? Number(match[2]) : null,
  };
};

export default function VariantsSection({ card, setCard }) {
  const [variantSearch, setVariantSearch] = useState("");
  const [variantResults, setVariantResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [focused, setFocused] = useState(false);
  const requestRef = useRef(0);
  const containerRef = useRef(null);

  const selected = Array.isArray(card.variations) ? card.variations : [];
  const term = variantSearch.trim();
  const numberedTerm = useMemo(() => parseNumberedVariantLabel(term), [term]);
  const searchTerm = numberedTerm?.name || term;

  useEffect(() => {
    const onOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setFocused(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  useEffect(() => {
    if (!focused || searchTerm.length < 2) {
      setVariantResults([]);
      setLoading(false);
      return undefined;
    }

    const requestId = ++requestRef.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      const { data, error } = await supabase.rpc("get_variants", {
        search: searchTerm,
      });

      if (requestId !== requestRef.current) return;

      if (error) {
        console.error("Variant search failed:", error);
        setVariantResults([]);
      } else {
        setVariantResults(Array.isArray(data) ? data : []);
      }

      setActiveIndex(-1);
      setLoading(false);
    }, 220);

    return () => clearTimeout(timer);
  }, [searchTerm, focused]);

  const exactCanonical = useMemo(
    () => variantResults.some((variant) => normalize(variant.name) === normalize(searchTerm)),
    [variantResults, searchTerm]
  );

  const alreadySelected = (candidate) =>
    selected.some((entry) => {
      if (candidate?.id && entry?.id) return Number(candidate.id) === Number(entry.id);
      return normalize(entry?.name || entry) === normalize(candidate?.name);
    });

  const addCanonical = (variant) => {
    if (!variant?.id || !variant?.name || alreadySelected(variant)) return;

    setCard((prev) => ({
      ...prev,
      // If the collector searched with a full copy number ("Red 3/5"),
      // capture 3 while linking the reusable "Red /5" variant. Do not
      // overwrite a serial number they already entered explicitly.
      serial_number:
        numberedTerm && !String(prev.serial_number ?? "").trim()
          ? String(numberedTerm.serialNumber)
          : prev.serial_number,
      variations: [
        ...(Array.isArray(prev.variations) ? prev.variations : []),
        {
          id: Number(variant.id),
          name: variant.name,
          canonical: true,
        },
      ],
    }));

    setVariantSearch("");
    setVariantResults([]);
    setActiveIndex(-1);
  };

  const addSuggestion = () => {
    const name = numberedTerm?.name || term;
    if (!name || exactCanonical) return;

    const candidate = { name, canonical: false };
    if (alreadySelected(candidate)) {
      setVariantSearch("");
      return;
    }

    setCard((prev) => ({
      ...prev,
      serial_number:
        numberedTerm && !String(prev.serial_number ?? "").trim()
          ? String(numberedTerm.serialNumber)
          : prev.serial_number,
      variations: [
        ...(Array.isArray(prev.variations) ? prev.variations : []),
        {
          id: null,
          name,
          canonical: false,
        },
      ],
    }));

    setVariantSearch("");
    setVariantResults([]);
    setActiveIndex(-1);
  };

  const removeVariant = (index) => {
    setCard((prev) => ({
      ...prev,
      variations: (Array.isArray(prev.variations) ? prev.variations : []).filter(
        (_, candidateIndex) => candidateIndex !== index
      ),
    }));
  };

  const options = [
    ...variantResults.map((variant) => ({ type: "canonical", variant })),
    ...(searchTerm.length >= 2 && !exactCanonical
      ? [{ type: "suggestion", name: numberedTerm?.name || term }]
      : []),
  ];

  const chooseOption = (option) => {
    if (option?.type === "canonical") addCanonical(option.variant);
    if (option?.type === "suggestion") addSuggestion();
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (options.length) {
        setActiveIndex((current) => (current + 1) % options.length);
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (options.length) {
        setActiveIndex((current) =>
          current <= 0 ? options.length - 1 : current - 1
        );
      }
    } else if (event.key === "Enter") {
      if (activeIndex >= 0 && options[activeIndex]) {
        event.preventDefault();
        chooseOption(options[activeIndex]);
      } else if (searchTerm.length >= 2 && !exactCanonical && !variantResults.length) {
        event.preventDefault();
        addSuggestion();
      }
    } else if (event.key === "Escape") {
      setFocused(false);
      setVariantResults([]);
      setActiveIndex(-1);
    }
  };

  const showMenu =
    focused && searchTerm.length >= 2 && (loading || options.length > 0);

  return (
    <div ref={containerRef} className="hobbli-variants-section">

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 3fr) minmax(110px, 1fr)",
          gap: 8,
          alignItems: "start",
        }}
      >
        <input
          type="text"
          value={variantSearch}
          onChange={(event) => setVariantSearch(event.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search or suggest a variant"
          className="form-input hobbli-variant-search"
          autoComplete="off"
        />

        <input
          type="text"
          inputMode="numeric"
          value={card.serial_number ?? ""}
          onChange={(event) =>
            setCard((prev) => ({ ...prev, serial_number: event.target.value }))
          }
          onBlur={(event) => {
            const parsed = parseSerialField(event.target.value);
            if (parsed && parsed.serialNumber >= 1) {
              setCard((prev) => ({
                ...prev,
                serial_number: String(parsed.serialNumber),
              }));
            }
          }}
          placeholder="Serial # (optional)"
          className="form-input hobbli-serial-number"
          autoComplete="off"
        />
      </div>

      {numberedTerm ? (
        <div style={{ marginTop: 4, fontSize: 11, opacity: 0.7 }}>
          We’ll use <strong>{numberedTerm.name}</strong> and save
          <strong> #{numberedTerm.serialNumber}</strong> as this copy’s serial number.
        </div>
      ) : (
        <div style={{ marginTop: 4, fontSize: 11, opacity: 0.65 }}>
          Numbered card? Choose the /# variant and enter your card’s serial #. You can also paste 3/5.
        </div>
      )}

      {showMenu && (
        <div
          style={{
            position: "absolute",
            zIndex: 1200,
            left: 0,
            right: "calc(25% + 6px)",
            top: 38,
            background: "var(--card-bg, #fff)",
            border: "1px solid rgba(127,127,127,.35)",
            borderRadius: 7,
            boxShadow: "0 8px 22px rgba(0,0,0,.18)",
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          {loading ? (
            <div style={{ padding: 10, opacity: 0.7 }}>Searching variants…</div>
          ) : (
            options.map((option, index) => {
              const isActive = activeIndex === index;
              if (option.type === "suggestion") {
                return (
                  <div
                    key={`suggest-${option.name}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      addSuggestion();
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    style={{
                      padding: "9px 11px",
                      cursor: "pointer",
                      borderTop: variantResults.length
                        ? "1px solid rgba(127,127,127,.22)"
                        : "none",
                      background: isActive ? "rgba(127,127,127,.14)" : "transparent",
                      fontWeight: 600,
                    }}
                  >
                    + Use “{option.name}"
                  </div>
                );
              }

              return (
                <div
                  key={option.variant.id}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    addCanonical(option.variant);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  style={{
                    padding: "9px 11px",
                    cursor: "pointer",
                    background: isActive ? "rgba(127,127,127,.14)" : "transparent",
                  }}
                >
                  <strong>{option.variant.name}</strong>
                  <span style={{ marginLeft: 7, fontSize: 11, opacity: 0.65 }}>
                    Hobbli variant
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}

      {selected.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 7,
            marginTop: 8,
          }}
        >
          {selected.map((variant, index) => (
            <span
              key={variant?.id ? `variant-${variant.id}` : `suggestion-${variant?.name}-${index}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 8px",
                borderRadius: 999,
                border: "1px solid rgba(127,127,127,.3)",
              }}
            >
              {variant?.name || variant}
              <button
                type="button"
                onClick={() => removeVariant(index)}
                aria-label={`Remove ${variant?.name || variant}`}
                style={{
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                  padding: 0,
                  color: "inherit",
                }}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
